import { AUTH_SERVICE_URL, OAUTH_CLIENT_ID } from "../config";
// PKCE / OAuth / JWT primitives shared verbatim by admin and liff.
// Pure functions + the TokenData shape; no storage, no React.

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function generatePKCE(): Promise<{
  codeVerifier: string;
  codeChallenge: string;
}> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const codeVerifier = arrayBufferToBase64Url(array.buffer);
  const encoder = new TextEncoder();
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(codeVerifier));
  const codeChallenge = arrayBufferToBase64Url(digest);
  return { codeVerifier, codeChallenge };
}

export function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return arrayBufferToBase64Url(array.buffer);
}

// OAuth client id/type are env-driven per vertical, with no hardcoded default;
// config.ts owns the read. Two names for one value: the auth service takes the
// same identifier as both, and the callers read better for saying which they
// mean.
export function getClientId(): string {
  return OAUTH_CLIENT_ID;
}

export function getClientType(): string {
  return OAUTH_CLIENT_ID;
}

export function getAuthorizeUrl(
  codeChallenge: string,
  state: string,
  redirectUri: string,
  lang?: string,
): string {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: getClientId(),
    redirect_uri: redirectUri,
    scope: "openid profile email",
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  if (lang === "en") params.set("lang", "en");
  return `${AUTH_SERVICE_URL}/authorize?${params.toString()}`;
}

/**
 * The `state` we send through RP-initiated logout and get handed back.
 *
 * post_logout_redirect_uri has to match a registered redirect_uri byte for byte,
 * and the only one every client registers is /auth/callback. So the sign-out
 * return lands on the OAuth callback route, and this marker is how that route
 * tells it apart from a real authorization redirect: a code-less arrival that
 * carries it is a completed logout, not the missing-code error.
 */
export const LOGOUT_STATE = "logout";

/**
 * RP-initiated logout (the auth service's end_session_endpoint).
 *
 * Clearing our own tokens is not a logout: the auth service's session cookie is
 * host-only on its origin, so only a browser redirect there can end it. Skip
 * this and the user signs out, clicks "log in", and is bounced straight back in
 * with no prompt, because first-party clients have consent switched off.
 *
 * The id_token is the hint that authorizes a no-interaction logout; without one
 * the auth service shows a confirmation page instead, which is the right
 * fallback for a session so old the token is gone. An expired id_token is fine
 * and expected — ours carry the 15-minute access-token TTL, and the endpoint
 * accepts an expired hint by design.
 */
export function getEndSessionUrl(
  idToken: string | null | undefined,
  redirectUri: string,
  lang?: string,
): string {
  const params = new URLSearchParams();
  if (idToken) params.set("id_token_hint", idToken);
  // Sent even with a hint present: the auth service needs it to resolve which
  // client's registered redirect_uris to validate the return URL against.
  params.set("client_id", getClientId());
  params.set("post_logout_redirect_uri", redirectUri);
  params.set("state", LOGOUT_STATE);
  if (lang === "en") params.set("lang", "en");
  return `${AUTH_SERVICE_URL}/end_session?${params.toString()}`;
}

export interface TokenData {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  expires_in: number;
  token_type?: string;
}

export function parseJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1];
    const binary = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const json = new TextDecoder().decode(Uint8Array.from(binary, c => c.charCodeAt(0)));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, bufferSeconds = 30): boolean {
  const payload = parseJwtPayload<{ exp?: number }>(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 > payload.exp - bufferSeconds;
}
